import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import Grid from "../Components/Grid";
import { useAxiosGet } from '../Hooks/HttpRequests';
import Box from '@mui/material/Box';
import { Divider, Paper, Tabs, Tab } from "@mui/material";
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRightLeft } from '@fortawesome/free-solid-svg-icons';


function Draft() {
    const navigate = useNavigate();
    const location = useLocation();

    const slugFromTab = { future: 'futuredrafts', past: 'pastdrafts' };
    const tabFromSlug = Object.fromEntries(Object.entries(slugFromTab).map(([k, v]) => [v, k]));

    const segments = location.pathname.split('/').filter(Boolean);
    const slug = segments[1] || '';
    const tab = tabFromSlug[slug] || 'future';

    React.useEffect(() => {
        if (segments[0] === 'draft' && !segments[1]) {
            navigate(`/draft/${slugFromTab[tab]}`, { replace: true });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const handleTabChange = (_e, v) => {
        const nextSlug = slugFromTab[v] || 'futuredrafts';
        if (nextSlug !== slug) navigate(`/draft/${nextSlug}`);
    };

    const base = 'https://supermegadynasty.com/smd/draft';
    const orderUrl = `${base}/2026/draft.json`;
    const pick1_url = `${base}/2026/draft_picks.json`;
    const pick2_url = `${base}/2027/draft_picks.json`;
    const pick3_url = `${base}/2028/draft_picks.json`;

    const order = useAxiosGet(orderUrl);
    const picks1 = useAxiosGet(pick1_url);
    const picks2 = useAxiosGet(pick2_url);
    const picks3 = useAxiosGet(pick3_url);

    // Sections for future drafts
    const sections = [
        { year: 2026, resp: picks1 },
        { year: 2027, resp: picks2 },
        { year: 2028, resp: picks3 },
    ];

    const anyError = sections.some(s => s.resp.error);

    return (
        <div className="mb-3">
            <Tabs
                value={tab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="draft tabs"
                textColor="primary"
            >
                <Tab value="future" label="Future Drafts" />
                <Tab value="past" label="Past Drafts" />
            </Tabs>
            <Divider />

            {tab === 'future' && (
                <div className="md:flex">
                    <Box>
                        <Paper
                            elevation={6}
                            sx={{
                                backgroundImage: 'none',
                                backgroundColor: (theme) =>
                                    theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
                            }}
                        >
                            {order.error && (
                                <div className="bg-red-300 p-3">Failed to load team order.</div>
                            )}
                            {!order.data && !order.error && (
                                <div className="p-3">Loading draft data…</div>
                            )}

                            {order.data && (
                                <>
                                    {anyError && (
                                        <div className="bg-yellow-700 text-white p-2 m-2 rounded">
                                            Some draft years failed to load; showing available years.
                                        </div>
                                    )}
                                    {sections.map((s) => (
                                        <div key={s.year}>
                                            <h1 className="font-bold text-2xl p-3">{s.year}</h1>
                                            {s.resp.data ? (
                                                <Grid picks={s.resp.data} teams={order.data} />
                                            ) : (
                                                <div className="p-3 text-sm text-gray-300">Loading…</div>
                                            )}
                                        </div>
                                    ))}
                                </>
                            )}
                        </Paper>
                    </Box>
                </div>
            )}

            {tab === 'past' && (
                <PastDrafts />
            )}
        </div>
    );
}

// Helper component: table per season with a draft selector
function SeasonDraftTable({ season, drafts, userNameById, ownerIdByRosterId, playerMap }) {
    const [selectedId, setSelectedId] = useState(() => (drafts[0] && drafts[0].draft_id) || '');
    const [filter, setFilter] = useState('');
    // Update default selection when drafts list changes
    React.useEffect(() => {
        if (drafts && drafts.length) setSelectedId(drafts[0].draft_id);
    }, [drafts]);

    const picksUrl = selectedId ? `https://api.sleeper.app/v1/draft/${selectedId}/picks` : null;
    const picksResp = useAxiosGet(picksUrl || '');

    const pickRows = Array.isArray(picksResp.data) ? picksResp.data : [];

    const resolveUsername = (row) => {
        const by = row?.picked_by ? String(row.picked_by) : null;
        if (by && userNameById && userNameById.has(by)) return userNameById.get(by);
        const rid = row?.roster_id;
        if (rid != null && ownerIdByRosterId) {
            const owner = ownerIdByRosterId.get(Number(rid));
            if (owner && userNameById && userNameById.has(String(owner))) {
                return userNameById.get(String(owner));
            }
        }
        return '';
    };

    const resolvePlayerName = (row) => {
        if (!row) return '';
        // Prefer explicit name fields from the pick if present
        const md = row.metadata || row.player || {};
        const direct = md.full_name || md.player_name || (
            (md.first_name || md.last_name) ? `${md.first_name || ''} ${md.last_name || ''}`.trim() : null
        );
        if (direct) return direct;
        // Try player map by id
        const pid = row.player_id || md.player_id || md.id;
        if (!pid) return '';
        const key = String(pid);
        const p = playerMap && playerMap[key];
        if (p) {
            if (p.full_name) return p.full_name;
            if (p.first_name || p.last_name) return `${p.first_name || ''} ${p.last_name || ''}`.trim();
            return p.name || key;
        }
        return key;
    };

    return (
        <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Typography variant="h6" component="div">{season}</Typography>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel id={`draft-select-${season}`}>Select Draft</InputLabel>
                    <Select
                        labelId={`draft-select-${season}`}
                        id={`draft-select-${season}`}
                        value={selectedId}
                        label="Select Draft"
                        onChange={(e) => setSelectedId(e.target.value)}
                    >
                        {drafts.map((d) => (
                            <MenuItem key={d.draft_id} value={d.draft_id}>
                                {d.name || `${d.season} ${d.type || ''}`.trim()} ({d.draft_id.slice(0,8)}…)
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    size="small"
                    label="Filter (username/player/original owner/round)"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                />
            </Box>

            {picksResp.loading && (
                <Typography variant="body2" sx={{ p: 1 }}>Loading picks…</Typography>
            )}
            {picksResp.error && (
                <Typography variant="body2" color="error" sx={{ p: 1 }}>Failed to load picks.</Typography>
            )}

            {!picksResp.loading && !picksResp.error && (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Round</TableCell>
                                <TableCell>Pick</TableCell>
                                <TableCell>Username</TableCell>
                                <TableCell>Player</TableCell>
                                {/* <TableCell>Player ID</TableCell> */}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pickRows
                                .filter((r) => {
                                    const q = (filter || '').trim().toLowerCase();
                                    if (!q) return true;
                                    const uname = (resolveUsername(r) || '').toLowerCase();
                                    const pname = (resolvePlayerName(r) || '').toLowerCase();
                                    const roundStr = r.round != null ? String(r.round) : '';
                                    return (
                                        uname.includes(q) ||
                                        pname.includes(q) ||
                                        roundStr.includes(q)
                                    );
                                })
                                .map((r, idx) => (
                                <TableRow key={idx}>
                                    <TableCell>{r.round ?? ''}</TableCell>
                                    <TableCell>{(r.draft_slot ?? r.pick ?? '')}</TableCell>
                                    <TableCell>{resolveUsername(r)}</TableCell>
                                    <TableCell>{resolvePlayerName(r)}</TableCell>
                                    {/* <TableCell>{r.player_id ?? ''}</TableCell> */}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Paper>
    );
}

function PastDrafts() {
    const leagueBySeason = {
        2020: '572103683850252288',
        2021: '650025951245193216',
        2022: '785959210730311680',
        2023: '920765875261632512',
        2024: '1048282530463465472',
        2025: '1180948099673747456',
        2026: '1313578306209091584',
    };

    // Drafts per season (hooks must be declared statically)
    const drafts2020 = useAxiosGet(`https://api.sleeper.app/v1/league/${leagueBySeason[2020]}/drafts`);
    const users2020 = useAxiosGet(`https://api.sleeper.app/v1/league/${leagueBySeason[2020]}/users`);
    const rosters2020 = useAxiosGet(`https://api.sleeper.app/v1/league/${leagueBySeason[2020]}/rosters`);

    const drafts2021 = useAxiosGet(`https://api.sleeper.app/v1/league/${leagueBySeason[2021]}/drafts`);
    const users2021 = useAxiosGet(`https://api.sleeper.app/v1/league/${leagueBySeason[2021]}/users`);
    const rosters2021 = useAxiosGet(`https://api.sleeper.app/v1/league/${leagueBySeason[2021]}/rosters`);

    const drafts2022 = useAxiosGet(`https://api.sleeper.app/v1/league/${leagueBySeason[2022]}/drafts`);
    const users2022 = useAxiosGet(`https://api.sleeper.app/v1/league/${leagueBySeason[2022]}/users`);
    const rosters2022 = useAxiosGet(`https://api.sleeper.app/v1/league/${leagueBySeason[2022]}/rosters`);

    const drafts2023 = useAxiosGet(`https://api.sleeper.app/v1/league/${leagueBySeason[2023]}/drafts`);
    const users2023 = useAxiosGet(`https://api.sleeper.app/v1/league/${leagueBySeason[2023]}/users`);
    const rosters2023 = useAxiosGet(`https://api.sleeper.app/v1/league/${leagueBySeason[2023]}/rosters`);

    const drafts2024 = useAxiosGet(`https://api.sleeper.app/v1/league/${leagueBySeason[2024]}/drafts`);
    const users2024 = useAxiosGet(`https://api.sleeper.app/v1/league/${leagueBySeason[2024]}/users`);
    const rosters2024 = useAxiosGet(`https://api.sleeper.app/v1/league/${leagueBySeason[2024]}/rosters`);

    const drafts2025 = useAxiosGet(`https://api.sleeper.app/v1/league/${leagueBySeason[2025]}/drafts`);
    const users2025 = useAxiosGet(`https://api.sleeper.app/v1/league/${leagueBySeason[2025]}/users`);
    const rosters2025 = useAxiosGet(`https://api.sleeper.app/v1/league/${leagueBySeason[2025]}/rosters`);

    const drafts2026 = useAxiosGet(leagueBySeason[2026] ? `https://api.sleeper.app/v1/league/${leagueBySeason[2026]}/drafts` : '');
    const users2026 = useAxiosGet(leagueBySeason[2026] ? `https://api.sleeper.app/v1/league/${leagueBySeason[2026]}/users` : '');
    const rosters2026 = useAxiosGet(leagueBySeason[2026] ? `https://api.sleeper.app/v1/league/${leagueBySeason[2026]}/rosters` : '');

    const playersResp = useAxiosGet('https://supermegadynasty.com/smd_all_players.json');
    const playerMap = playersResp && playersResp.data && typeof playersResp.data === 'object' ? playersResp.data : null;

    // Removed legacy prior-year owner mapping that relied on smd_*.json

    // UI state: selected season and draft, and filter query
    const [selectedSeason, setSelectedSeason] = useState(null);
    const [selectedDraftId, setSelectedDraftId] = useState('');
    const [filter, setFilter] = useState('');

    // Build season entries (descending order)
    const entries = [
        { season: 2026, drafts: drafts2026, users: users2026, rosters: rosters2026 },
        { season: 2025, drafts: drafts2025, users: users2025, rosters: rosters2025 },
        { season: 2024, drafts: drafts2024, users: users2024, rosters: rosters2024 },
        { season: 2023, drafts: drafts2023, users: users2023, rosters: rosters2023 },
        { season: 2022, drafts: drafts2022, users: users2022, rosters: rosters2022 },
        { season: 2021, drafts: drafts2021, users: users2021, rosters: rosters2021 },
        { season: 2020, drafts: drafts2020, users: users2020, rosters: rosters2020 },
    ];

    const anyLoading = entries.some(e => !e.drafts.data && !e.drafts.error);
    const anyError = entries.some(e => e.drafts.error);

    // Build fast lookup per season for drafts and mapping helpers
    const seasonData = useMemo(() => {
        const out = new Map();
        entries.forEach((e) => {
            const draftsArr = Array.isArray(e.drafts.data) ? e.drafts.data : [];
            const complete = draftsArr.filter(d => (d.status ? d.status === 'complete' : true));
            const usersArr = Array.isArray(e.users.data) ? e.users.data : [];
            const rostersArr = Array.isArray(e.rosters.data) ? e.rosters.data : [];
            const userNameById = new Map(usersArr.map(u => [String(u.user_id), u.display_name || u.username || String(u.user_id)]));
            const ownerIdByRosterId = new Map(rostersArr.map(r => [Number(r.roster_id), r.owner_id != null ? String(r.owner_id) : null]));
            out.set(e.season, { drafts: complete, userNameById, ownerIdByRosterId });
        });
        return out;
    }, [entries.map(e => e.drafts.data).join('|'), entries.map(e => e.users.data).join('|'), entries.map(e => e.rosters.data).join('|')]);

    const availableSeasons = Array.from(seasonData.keys()).sort((a, b) => Number(b) - Number(a));

    // Initialize defaults when data arrives
    React.useEffect(() => {
        if (!selectedSeason && availableSeasons.length) {
            setSelectedSeason(availableSeasons[0]);
        }
    }, [availableSeasons, selectedSeason]);

    React.useEffect(() => {
        if (!selectedSeason) return;
        const sd = seasonData.get(selectedSeason);
        const drafts = sd ? sd.drafts : [];
        if (!drafts || drafts.length === 0) return;
        // If current selected draft not in list, reset to first
        const exists = drafts.some(d => d.draft_id === selectedDraftId);
        if (!exists) {
            setSelectedDraftId(drafts[0].draft_id);
        }
    }, [selectedSeason, seasonData, selectedDraftId]);

    // Resolve helpers for current season
    const current = selectedSeason ? seasonData.get(selectedSeason) : null;
    const currentDrafts = current ? current.drafts : [];
    const userNameById = current ? current.userNameById : new Map();
    const ownerIdByRosterId = current ? current.ownerIdByRosterId : new Map();

    // Picks for selected draft
    const picksUrl = selectedDraftId ? `https://api.sleeper.app/v1/draft/${selectedDraftId}/picks` : '';
    const picksResp = useAxiosGet(picksUrl);

    const resolveUsername = (row) => {
        const by = row?.picked_by ? String(row.picked_by) : null;
        if (by && userNameById && userNameById.has(by)) return userNameById.get(by);
        const rid = row?.roster_id;
        if (rid != null && ownerIdByRosterId) {
            const owner = ownerIdByRosterId.get(Number(rid));
            if (owner && userNameById && userNameById.has(String(owner))) {
                return userNameById.get(String(owner));
            }
        }
        return '';
    };

    const resolvePlayerName = (row) => {
        if (!row) return '';
        const md = row.metadata || row.player || {};
        const direct = md.full_name || md.player_name || (
            (md.first_name || md.last_name) ? `${md.first_name || ''} ${md.last_name || ''}`.trim() : null
        );
        if (direct) return direct;
        const pid = row.player_id || md.player_id || md.id;
        if (!pid) return '';
        const key = String(pid);
        const p = playerMap && playerMap[key];
        if (p) {
            if (p.full_name) return p.full_name;
            if (p.first_name || p.last_name) return `${p.first_name || ''} ${p.last_name || ''}`.trim();
            return p.name || key;
        }
        return key;
    };

    // Draft metadata for Original Owner mapping using Sleeper API
    const draftMetaUrl = selectedDraftId ? `https://api.sleeper.app/v1/draft/${selectedDraftId}` : '';
    const draftMetaResp = useAxiosGet(draftMetaUrl);
    const slotOwnerBySlot = useMemo(() => {
        const map = new Map();
        const meta = draftMetaResp && draftMetaResp.data ? draftMetaResp.data : {};
        // Prefer slot_to_roster_id mapping when available
        const slotToRoster = meta && (meta.slot_to_roster_id || meta.slot_to_roster);
        if (slotToRoster && typeof slotToRoster === 'object') {
            Object.entries(slotToRoster).forEach(([slot, rid]) => {
                const slotNum = Number(slot);
                const rosterId = Number(rid);
                const ownerId = ownerIdByRosterId.get(rosterId);
                const name = ownerId ? userNameById.get(String(ownerId)) : null;
                if (slotNum && name) map.set(slotNum, name);
            });
            return map;
        }
        // Fallback: draft_order maps user_id -> slot
        const draftOrder = meta && meta.draft_order;
        if (draftOrder && typeof draftOrder === 'object') {
            Object.entries(draftOrder).forEach(([userId, slot]) => {
                const slotNum = Number(slot);
                const name = userNameById.get(String(userId));
                if (slotNum && name) map.set(slotNum, name);
            });
        }
        return map;
    }, [draftMetaResp.data, ownerIdByRosterId, userNameById]);

    return (
        <div className="p-3">
            <Typography variant="h6" sx={{ mb: 1 }}>Past Drafts</Typography>
            {anyError && (
                <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                    <Typography color="error">Some seasons failed to load drafts.</Typography>
                </Paper>
            )}
            {anyLoading && (
                <Typography variant="body2" sx={{ mb: 2 }}>Loading drafts…</Typography>
            )}

            {/* Controls: Season, Draft, Filter */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel id="season-select">Season</InputLabel>
                    <Select
                        labelId="season-select"
                        id="season-select"
                        value={selectedSeason || ''}
                        label="Season"
                        onChange={(e) => setSelectedSeason(e.target.value)}
                    >
                        {availableSeasons.map((s) => (
                            <MenuItem key={s} value={s}>{s}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 260 }}>
                    <InputLabel id="draft-select">Select Draft</InputLabel>
                    <Select
                        labelId="draft-select"
                        id="draft-select"
                        value={selectedDraftId}
                        label="Select Draft"
                        onChange={(e) => setSelectedDraftId(e.target.value)}
                    >
                        {currentDrafts.map((d) => (
                            <MenuItem key={d.draft_id} value={d.draft_id}>
                                {d.name || `${d.season} ${d.type || ''}`.trim()} ({d.draft_id.slice(0,8)}…)
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    size="small"
                    label="Filter (username/player/original owner/round)"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                />
                <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                        const rows = (Array.isArray(picksResp.data) ? picksResp.data : [])
                            .filter((r) => {
                                const q = (filter || '').trim().toLowerCase();
                                if (!q) return true;
                                const uname = (resolveUsername(r) || '').toLowerCase();
                                const pname = (resolvePlayerName(r) || '').toLowerCase();
                                const roundStr = r.round != null ? String(r.round) : '';
                                const pickNum = Number(r.draft_slot ?? r.pick ?? 0);
                                const orig = (slotOwnerBySlot.get(pickNum) || '').toLowerCase();
                                return (
                                    uname.includes(q) ||
                                    pname.includes(q) ||
                                    orig.includes(q) ||
                                    roundStr.includes(q)
                                );
                            })
                            .map((r) => {
                                const pickNum = Number(r.draft_slot ?? r.pick ?? 0);
                                const originalOwner = slotOwnerBySlot.get(pickNum) || '';
                                const username = resolveUsername(r) || '';
                                const player = resolvePlayerName(r) || '';
                                const traded = (
                                    originalOwner && username &&
                                    originalOwner.trim().toLowerCase() !== username.trim().toLowerCase()
                                );
                                return {
                                    round: r.round ?? null,
                                    pick: r.draft_slot ?? r.pick ?? null,
                                    username,
                                    player,
                                    original_owner: originalOwner,
                                    traded,
                                };
                            });
                        const filename = `past_draft_${selectedSeason || 'unknown'}_${(selectedDraftId || '').slice(0,8)}.json`;
                        const blob = new Blob([JSON.stringify(rows, null, 2)], { type: 'application/json' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = filename;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        URL.revokeObjectURL(url);
                    }}
                    disabled={!Array.isArray(picksResp.data) || picksResp.data.length === 0}
                >
                    Download JSON
                </Button>
            </Box>

            {/* Single table for selected draft */}
            {selectedDraftId && !picksResp.loading && !picksResp.error && (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Round</TableCell>
                                <TableCell>Pick</TableCell>
                                <TableCell>Username</TableCell>
                                <TableCell>Player</TableCell>
                                <TableCell>Original Owner</TableCell>
                                <TableCell>Traded Pick</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(Array.isArray(picksResp.data) ? picksResp.data : [])
                                .filter((r) => {
                                    const q = (filter || '').trim().toLowerCase();
                                    if (!q) return true;
                                    const uname = (resolveUsername(r) || '').toLowerCase();
                                    const pname = (resolvePlayerName(r) || '').toLowerCase();
                                    const roundStr = r.round != null ? String(r.round) : '';
                                    const pickNum = Number(r.draft_slot ?? r.pick ?? 0);
                                    const orig = (slotOwnerBySlot.get(pickNum) || '').toLowerCase();
                                    return (
                                        uname.includes(q) ||
                                        pname.includes(q) ||
                                        orig.includes(q) ||
                                        roundStr.includes(q)
                                    );
                                })
                                .map((r, idx) => {
                                    // Original Owner is derived from the draft's slot mapping
                                    const pickNum = Number(r.draft_slot ?? r.pick ?? 0);
                                    const originalOwner = slotOwnerBySlot.get(pickNum) || '';
                                    const username = resolveUsername(r) || '';
                                    const traded = (
                                        originalOwner && username &&
                                        originalOwner.trim().toLowerCase() !== username.trim().toLowerCase()
                                    );
                                    return (
                                        <TableRow key={idx}>
                                            <TableCell>{r.round ?? ''}</TableCell>
                                            <TableCell>{(r.draft_slot ?? r.pick ?? '')}</TableCell>
                                            <TableCell>{username}</TableCell>
                                            <TableCell>{resolvePlayerName(r)}</TableCell>
                                            <TableCell>{originalOwner}</TableCell>
                                            <TableCell>
                                                {traded ? <FontAwesomeIcon icon={faRightLeft} title="Traded Pick" /> : ''}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            {selectedDraftId && picksResp.loading && (
                <Typography variant="body2">Loading picks…</Typography>
            )}
            {selectedDraftId && picksResp.error && (
                <Typography variant="body2" color="error">Failed to load picks.</Typography>
            )}
        </div>
    );
}

export default Draft;
export { PastDrafts };
